import { useState, useEffect } from "react";
import { api } from "@/App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  UserCog,
  Plus,
  RefreshCw,
  Trash2,
  Edit,
  Mail,
  Building2,
  Loader2,
  UserX,
  UserPlus,
  Eye,
  EyeOff,
} from "lucide-react";

export default function ManagersPage() {
  const [managers, setManagers] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    concession_ids: [],
    send_email: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [managersRes, concessionsRes] = await Promise.all([
        api.get("/managers"),
        api.get("/concessions"),
      ]);
      setManagers(managersRes.data);
      setConcessions(concessionsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const symbols = "#@!$";
    let password = "";
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    password += symbols.charAt(Math.floor(Math.random() * symbols.length));
    return password;
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: generatePassword(),
      concession_ids: [],
      send_email: true,
    });
    setShowPassword(false);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openReplaceDialog = (manager) => {
    setSelectedManager(manager);
    setFormData({
      name: "",
      email: "",
      password: generatePassword(),
      concession_ids: manager.concession_ids || (manager.concession_id ? [manager.concession_id] : []),
      send_email: true,
    });
    setShowReplaceDialog(true);
  };

  const openEditDialog = (manager) => {
    setSelectedManager(manager);
    setFormData({
      name: manager.name,
      email: manager.email,
      password: "",
      concession_ids: manager.concession_ids || (manager.concession_id ? [manager.concession_id] : []),
      send_email: false,
    });
    setShowEditDialog(true);
  };

  const handleCreateManager = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setFormLoading(true);
    try {
      const res = await api.post("/managers", formData);
      toast.success(res.data.message);
      if (res.data.email_sent) {
        toast.info("Email envoyé au nouveau manager");
      }
      setShowCreateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setFormLoading(false);
    }
  };

  const handleReplaceManager = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setFormLoading(true);
    try {
      const res = await api.post(`/managers/${selectedManager.id}/replace`, formData);
      toast.success(res.data.message);
      if (res.data.email_sent) {
        toast.info("Email envoyé au nouveau manager");
      }
      setShowReplaceDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du remplacement");
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditManager = async () => {
    setFormLoading(true);
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        concession_ids: formData.concession_ids,
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await api.put(`/managers/${selectedManager.id}`, updateData);
      toast.success("Manager mis à jour avec succès");
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivateManager = async (manager) => {
    if (!confirm(`Êtes-vous sûr de vouloir désactiver ${manager.name} ?`)) {
      return;
    }

    try {
      await api.delete(`/managers/${manager.id}`);
      toast.success("Manager désactivé avec succès");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la désactivation");
    }
  };

  const toggleConcession = (concessionId) => {
    setFormData((prev) => ({
      ...prev,
      concession_ids: prev.concession_ids.includes(concessionId)
        ? prev.concession_ids.filter((id) => id !== concessionId)
        : [...prev.concession_ids, concessionId],
    }));
  };

  const activeManagers = managers.filter((m) => m.is_active !== false);
  const inactiveManagers = managers.filter((m) => m.is_active === false);

  return (
    <Layout>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#212E38]">Gestion des Managers</h1>
          <p className="text-gray-500 mt-1">
            Créer, modifier ou remplacer les managers de vos concessions
          </p>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="create-manager-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Manager
        </Button>
      </div>

      {/* Active Managers */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <UserCog className="w-5 h-5 text-[#212E38]" />
          <h2 className="text-lg font-semibold">Managers actifs ({activeManagers.length})</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : activeManagers.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Aucun manager actif</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Concessions</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeManagers.map((manager) => (
                <TableRow key={manager.id} data-testid={`manager-row-${manager.id}`}>
                  <TableCell className="font-medium">{manager.name}</TableCell>
                  <TableCell>{manager.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {manager.concessions?.map((c) => (
                        <span
                          key={c.id}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800"
                        >
                          <Building2 className="w-3 h-3 mr-1" />
                          {c.name}
                        </span>
                      ))}
                      {(!manager.concessions || manager.concessions.length === 0) && (
                        <span className="text-gray-400 text-sm">Aucune concession</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(manager)}
                        data-testid={`edit-manager-${manager.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openReplaceDialog(manager)}
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        data-testid={`replace-manager-${manager.id}`}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeactivateManager(manager)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`deactivate-manager-${manager.id}`}
                      >
                        <UserX className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Inactive Managers */}
      {inactiveManagers.length > 0 && (
        <div className="card bg-gray-50">
          <div className="flex items-center gap-2 mb-4">
            <UserX className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-600">
              Anciens managers ({inactiveManagers.length})
            </h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Remplacé par</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inactiveManagers.map((manager) => (
                <TableRow key={manager.id} className="text-gray-500">
                  <TableCell>{manager.name}</TableCell>
                  <TableCell>{manager.email}</TableCell>
                  <TableCell>{manager.replaced_by || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create Manager Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Nouveau Manager
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Jean Dupont"
                data-testid="manager-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="jean.dupont@verbaere.com"
                data-testid="manager-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    data-testid="manager-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormData({ ...formData, password: generatePassword() })}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Concessions</Label>
              <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                {concessions.filter(c => c.is_active !== false).map((concession) => (
                  <div key={concession.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`concession-${concession.id}`}
                      checked={formData.concession_ids.includes(concession.id)}
                      onCheckedChange={() => toggleConcession(concession.id)}
                    />
                    <label
                      htmlFor={`concession-${concession.id}`}
                      className="text-sm cursor-pointer"
                    >
                      {concession.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Checkbox
                id="send_email"
                checked={formData.send_email}
                onCheckedChange={(checked) => setFormData({ ...formData, send_email: checked })}
              />
              <label htmlFor="send_email" className="text-sm cursor-pointer flex items-center gap-1">
                <Mail className="w-4 h-4" />
                Envoyer les identifiants par email
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateManager} disabled={formLoading} className="btn-primary">
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Créer le manager
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace Manager Dialog */}
      <Dialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Remplacer {selectedManager?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
              <p className="text-sm text-orange-800">
                <strong>{selectedManager?.name}</strong> sera désactivé et remplacé par le nouveau manager.
                Toutes ses concessions seront transférées automatiquement.
              </p>
            </div>

            <div className="bg-gray-50 rounded-md p-3">
              <p className="text-sm text-gray-600 font-medium mb-2">Concessions transférées :</p>
              <div className="flex flex-wrap gap-1">
                {selectedManager?.concessions?.map((c) => (
                  <span key={c.id} className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                    {c.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_name">Nom du nouveau manager *</Label>
              <Input
                id="new_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Jean Dupont"
                data-testid="replace-manager-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_email">Email du nouveau manager *</Label>
              <Input
                id="new_email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="nouveau.manager@verbaere.com"
                data-testid="replace-manager-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_password">Mot de passe *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="new_password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    data-testid="replace-manager-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormData({ ...formData, password: generatePassword() })}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Checkbox
                id="send_email_replace"
                checked={formData.send_email}
                onCheckedChange={(checked) => setFormData({ ...formData, send_email: checked })}
              />
              <label htmlFor="send_email_replace" className="text-sm cursor-pointer flex items-center gap-1">
                <Mail className="w-4 h-4" />
                Envoyer les identifiants par email
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReplaceDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleReplaceManager} disabled={formLoading} className="bg-orange-600 hover:bg-orange-700">
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Remplacer le manager
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Manager Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifier {selectedManager?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_name">Nom</Label>
              <Input
                id="edit_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="edit-manager-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="edit-manager-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_password">Nouveau mot de passe (optionnel)</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="edit_password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Laisser vide pour ne pas changer"
                    data-testid="edit-manager-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormData({ ...formData, password: generatePassword() })}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Concessions</Label>
              <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                {concessions.filter(c => c.is_active !== false).map((concession) => (
                  <div key={concession.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`edit-concession-${concession.id}`}
                      checked={formData.concession_ids.includes(concession.id)}
                      onCheckedChange={() => toggleConcession(concession.id)}
                    />
                    <label
                      htmlFor={`edit-concession-${concession.id}`}
                      className="text-sm cursor-pointer"
                    >
                      {concession.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleEditManager} disabled={formLoading} className="btn-primary">
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
